import * as React from 'react'
import { motion, Variants } from 'framer-motion'
import styled from 'styled-components'
import { Home, List, Drafts } from '@styled-icons/material'
import { NavLink } from 'react-router-dom'
import { usePendingSubmissions, useDrafts } from '@oneblink/apps-react'
import { useMenuState } from './MenuStateProvider'

interface DisplayProps {
  direction: 'row' | 'column'
}

const textOpenClosedVariants: Variants = {
  openWidth: {
    opacity: 1,
    visibility: 'visible',
    transition: {
      ease: 'linear',
      duration: 0.3,
    },
  },
  closedWidth: {
    opacity: 0,
    transition: {
      ease: 'linear',
      duration: 0.1,
    },
  },
  openHeight: {
    opacity: 1,
    visibility: 'visible',
    transition: {
      ease: 'linear',
      duration: 0.3,
    },
  },
  closedHeight: {
    opacity: 0,
    transition: {
      ease: 'linear',
      duration: 0.1,
    },
  },
}

const MenuList = motion(styled.ul`
  padding: 16px;
`)
MenuList.displayName = 'MenuList'

const MenuListItem = styled.li<DisplayProps>`
  margin-bottom: ${({ theme }) => theme.unit / 2}px;
  display: block;
  min-width: 200px;
`
MenuListItem.displayName = 'MenuListItem'

const MenuButton = styled(NavLink)<DisplayProps>`
  font-size: 1.2rem;
  font-weight: 600;
  align-items: flex-end;
  color: ${({ theme }) => theme.palette.nswBrandDark};
  padding: 0.8rem;
  position: relative;
  flex-direction: row;
  display: flex;

  > * {
    flex: 1 1 auto;
  }

  > svg {
    flex: 0 0 auto;
  }

  &:hover {
    color: ${({ theme }) => theme.palette.nswBrandDark};
  }
`
MenuButton.displayName = 'MenuButton'

const MenuTextAnimated = motion(styled.span<DisplayProps>`
  display: inline-block;

  white-space: nowrap;
  vertical-align: middle;
  padding-left: ${({ theme }) => theme.unit}px;
`)
MenuTextAnimated.displayName = 'MenuTextAnimated'

const MenuText = ({
  children,
  ...props
}: React.PropsWithChildren<DisplayProps>) => (
  <MenuTextAnimated {...props} variants={textOpenClosedVariants}>
    {children}
  </MenuTextAnimated>
)
MenuText.displayName = 'MenuText'

const BadgeContainer = styled.span`
  margin-left: auto;
`
BadgeContainer.displayName = 'BadgeContainer'

const Badge = styled.span`
  border-radius: 9999px;
  color: ${({ theme }) => theme.palette.nswTextLight};
  background: ${({ theme }) => theme.palette.nswBrandDark};
  padding: 0 0.5rem;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
`
Badge.displayName = 'Badge'

const MenuItemWithBadge = styled.div`
  display: flex;
`
MenuItemWithBadge.displayName = 'MenuItemWithBadge'

const activeMenuItemStyle = {
  backgroundColor: 'var(--nsw-palette-grey-04)',
  fontWeight: 400,
  color: 'var(--nsw-brand-dark)',
}

export default function MenuItems() {
  const { position, toggleCollapsed } = useMenuState()
  const direction: DisplayProps['direction'] =
    ['left', 'right'].indexOf(position) > -1 ? 'row' : 'column'

  const onClick = React.useCallback(() => {
    if (['top', 'bottom'].indexOf(position) > -1) {
      toggleCollapsed()
    }
  }, [position, toggleCollapsed])

  const { pendingSubmissions } = usePendingSubmissions()
  const { drafts } = useDrafts()

  return (
    <MenuList layout>
      <MenuListItem direction={direction}>
        <MenuButton
          to="/"
          exact
          title="Main menu"
          direction={direction}
          activeStyle={activeMenuItemStyle}
          onClick={onClick}
        >
          <Home size={25} />
          <MenuText direction={direction}>Main menu</MenuText>
        </MenuButton>
      </MenuListItem>
      <MenuListItem direction={direction}>
        <MenuButton
          to="/pending"
          title="Pending Queue"
          direction={direction}
          activeStyle={activeMenuItemStyle}
          onClick={onClick}
        >
          <List size={25} />
          <MenuText direction={direction}>
            <MenuItemWithBadge>
              <span>Pending Queue</span>
              {pendingSubmissions.length > 0 && (
                <BadgeContainer>
                  <Badge>{pendingSubmissions.length}</Badge>
                </BadgeContainer>
              )}
            </MenuItemWithBadge>
          </MenuText>
        </MenuButton>
      </MenuListItem>
      <MenuListItem direction={direction}>
        <MenuButton
          to="/drafts"
          title="Drafts"
          direction={direction}
          activeStyle={activeMenuItemStyle}
          onClick={onClick}
        >
          <Drafts size={25} />
          <MenuText direction={direction}>
            <MenuItemWithBadge>
              <span>Drafts</span>
              {drafts.length > 0 && (
                <BadgeContainer>
                  <Badge>{drafts.length}</Badge>
                </BadgeContainer>
              )}
            </MenuItemWithBadge>
          </MenuText>
        </MenuButton>
      </MenuListItem>
    </MenuList>
  )
}
